import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import JSZip from "jszip";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PageHeader } from "@/components/ui/PageHeader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { MapPin, Calendar, User, Building2, Camera, FileSignature, Search, Filter, Download, ExternalLink, Trash2, Images, Star, CheckSquare, X, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { IMAGE_SIZES } from "@/config/images";
import { http } from "@/lib/http";
import { apiJson } from "@/lib/api";
import { YearFilter } from "@/components/filters/YearFilter";
import { useYear, useHydrateYearFromStorage } from "@/state/yearStore";
import { compareCyclesNewestFirst } from "@/lib/bookingCycles";
import {
  buildGalleryDownloadEntryFilename,
  buildGalleryDownloadImageBasename,
  buildGalleryDownloadZipFilename,
  resolveGalleryDownloadNamingMode,
} from "@shared/gallery-download-filenames";

interface ImageData {
  id: number;
  type: 'photo' | 'signature';
  url: string;
  assetUrl?: string;
  thumbnailUrl?: string;
  downloadUrl?: string;
  publicId?: string;
  assignmentId: number;
  campaignName: string;
  storeName: string;
  operatorName: string;
  cycleLabel?: string;
  notes?: string;
  latitude?: number;
  longitude?: number;
  createdAt: Date;
  storeOwner?: string;
  isFavourite?: boolean;
  reviewStatus?: 'rejected' | 'approved' | 'retailer_ready' | 'admin_recovery' | null;
  gallerySource?: 'operator' | 'admin_recovery';
  attributionLabel?: string | null;
  recordedByAdminName?: string | null;
}

// Photo-level favourite plus readiness/recovery. reviewStatus alone is
// submission-level for operator photos and always admin_recovery for recovery photos.
function isGalleryPhotoRetailerReadyComplete(
  image: Pick<ImageData, 'isFavourite' | 'reviewStatus' | 'gallerySource'>,
): boolean {
  if (!image.isFavourite) return false;
  if (image.gallerySource === 'admin_recovery' || image.reviewStatus === 'admin_recovery') {
    return true;
  }
  return image.reviewStatus === 'retailer_ready';
}

function mapGalleryImage(img: any): ImageData {
  const isRecovery =
    img.gallerySource === 'admin_recovery' || img.reviewStatus === 'admin_recovery';
  const operatorName = img.operatorName || 'operator';

  return {
    ...img,
    type: img.assetUrl?.includes('signature') ? 'signature' : 'photo',
    url: img.assetUrl,
    thumbnailUrl: img.thumbnailUrl || img.assetUrl,
    downloadUrl: img.downloadUrl || img.assetUrl,
    createdAt: new Date(img.createdAt),
    storeOwner: img.retailerName,
    assignmentId: img.assignmentId || img.eventId || img.id,
    cycleLabel: img.cycleLabel || '—',
    gallerySource: isRecovery ? 'admin_recovery' : 'operator',
    attributionLabel:
      img.attributionLabel ||
      (isRecovery ? `ReAction Admin on behalf of ${operatorName}` : null),
    recordedByAdminName: img.recordedByAdminName || img.meta?.adminName || null,
  };
}

interface FilterState {
  retailer: string;
  cycle: string;
  campaign: string;
  operator: string;
  store: string;
  type: string;
  taskType: string;
  reviewStatus: string;
  search: string;
}

export default function ImageGallery() {
  // Global year filter — consistent with campaigns, cycles dashboard, etc.
  useHydrateYearFromStorage();
  const year = useYear();

  const [filters, setFilters] = useState<FilterState>({
    retailer: 'all',
    cycle: 'all',
    campaign: 'all',
    operator: 'all',
    store: 'all',
    type: 'all',
    taskType: 'all',
    reviewStatus: 'all',
    search: ''
  });
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const pendingLightboxIndexRef = useRef<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [selectedImagesById, setSelectedImagesById] = useState<Map<number, ImageData>>(new Map());
  const [isSelectingAll, setIsSelectingAll] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const abortRef = useRef<AbortController | null>(null);

  const toggleSelection = useCallback((image: ImageData) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(image.id)) next.delete(image.id);
      else next.add(image.id);
      return next;
    });
    setSelectedImagesById(prev => {
      const next = new Map(prev);
      if (next.has(image.id)) {
        next.delete(image.id);
      } else {
        next.set(image.id, image);
      }
      return next;
    });
  }, []);

  const exitSelectionMode = useCallback(() => {
    setSelectionMode(false);
    setSelectedIds(new Set());
    setSelectedImagesById(new Map());
  }, []);

  // Reset selection when filters/year change (but preserve across pagination)
  useEffect(() => {
    setSelectedIds(new Set());
    setSelectedImagesById(new Map());
    setSelectionMode(false);
  }, [filters, year]);

  const buildGalleryParams = useCallback((page: number, limit: number) => {
    return new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      year: year.toString(),
      ...(filters.retailer !== 'all' && { retailer: filters.retailer }),
      ...(filters.cycle !== 'all' && { cycle: filters.cycle }),
      ...(filters.campaign !== 'all' && { campaign: filters.campaign }),
      ...(filters.operator !== 'all' && { operator: filters.operator }),
      ...(filters.store !== 'all' && { store: filters.store }),
      ...(filters.type !== 'all' && { type: filters.type }),
      ...(filters.taskType !== 'all' && { taskType: filters.taskType }),
      ...(filters.reviewStatus !== 'all' && { reviewStatus: filters.reviewStatus }),
      ...(filters.search && { search: filters.search })
    });
  }, [filters, year]);

  // Delete image mutation
  const deleteImageMutation = useMutation({
    mutationFn: async (imageId: number) => {
      await apiRequest(`/api/images/${imageId}`, "DELETE");
    },
    onSuccess: () => {
      toast({
        title: "Image deleted",
        description: "The image has been successfully deleted.",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/gallery-proxy'] });
    },
    onError: (error) => {
      toast({
        title: "Error deleting image",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const markRetailerReadyMutation = useMutation({
    mutationFn: async (imageIds: number[]) => {
      const response = await http('/api/images/mark-retailer-ready', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageIds }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || 'Failed to mark images retailer-ready');
      }
      return data as {
        markedImageCount: number;
        markedSubmissionCount: number;
        skipped?: {
          missing?: number;
          nonImage?: number;
          notApproved?: number;
        };
      };
    },
    onSuccess: (data) => {
      const skippedCount = (data.skipped?.missing || 0) + (data.skipped?.nonImage || 0) + (data.skipped?.notApproved || 0);
      toast({
        title: "Retailer ready updated",
        description: skippedCount > 0
          ? `${data.markedImageCount} image${data.markedImageCount === 1 ? '' : 's'} marked. ${skippedCount} skipped because they were not eligible.`
          : `${data.markedImageCount} image${data.markedImageCount === 1 ? '' : 's'} marked as favourite and retailer ready.`,
      });
      queryClient.invalidateQueries({ queryKey: ['/api/gallery-proxy'] });
      queryClient.invalidateQueries({ queryKey: ["campaign-favourite-images"] });
      exitSelectionMode();
    },
    onError: (error: Error) => {
      toast({
        title: "Unable to mark retailer ready",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // ─── Cascading filter data fetches ───────────────────────────────────

  // 1. Retailers (always available)
  const { data: retailers = [] } = useQuery<any[]>({
    queryKey: ['/api/retailers'],
    queryFn: async () => apiJson('/api/retailers'),
    staleTime: 60_000,
  });

  // 2. Cycles — depends on retailer selection; filtered by year client-side
  const retailerSelected = filters.retailer !== 'all';
  const { data: rawCycles = [] } = useQuery<any[]>({
    queryKey: ['/api/retailers', filters.retailer, 'cycles'],
    queryFn: async () => apiJson(`/api/retailers/${filters.retailer}/cycles`),
    enabled: retailerSelected,
    staleTime: 60_000,
  });

  // Filter cycles by selected year so dropdown only shows relevant cycles
  const cycles = useMemo(() => {
    if (!Array.isArray(rawCycles)) return [];
    return rawCycles.filter((c: any) => {
      const startDate = c?.start_date ?? c?.startDate;
      const endDate = c?.end_date ?? c?.endDate;
      if (startDate || endDate) {
        const startYear = startDate ? new Date(startDate).getFullYear() : null;
        const endYear = endDate ? new Date(endDate).getFullYear() : null;
        return startYear === year || endYear === year;
      }
      return true;
    }).sort(compareCyclesNewestFirst);
  }, [rawCycles, year]);

  // 3. Campaigns — depends on selected gallery evidence, including removal source campaigns.
  const cycleSelected = filters.cycle !== 'all';
  const { data: campaignsResponse } = useQuery({
    queryKey: ['/api/gallery-proxy/campaigns', year, filters.retailer, filters.cycle, filters.operator, filters.store, filters.type, filters.taskType, filters.reviewStatus, filters.search],
    queryFn: async () => {
      const params = new URLSearchParams({
        year: year.toString(),
        ...(filters.retailer !== 'all' && { retailer: filters.retailer }),
        ...(filters.cycle !== 'all' && { cycle: filters.cycle }),
        ...(filters.operator !== 'all' && { operator: filters.operator }),
        ...(filters.store !== 'all' && { store: filters.store }),
        ...(filters.type !== 'all' && { type: filters.type }),
        ...(filters.taskType !== 'all' && { taskType: filters.taskType }),
        ...(filters.reviewStatus !== 'all' && { reviewStatus: filters.reviewStatus }),
        ...(filters.search && { search: filters.search }),
      });
      return apiJson(`/api/gallery-proxy/campaigns?${params}`);
    },
    enabled: cycleSelected,
    staleTime: 60_000,
  });
  const campaigns = useMemo(() => {
    if (!campaignsResponse) return [];
    if (Array.isArray(campaignsResponse)) return campaignsResponse;
    if ((campaignsResponse as any).data && Array.isArray((campaignsResponse as any).data)) return (campaignsResponse as any).data;
    return [];
  }, [campaignsResponse]);

  const galleryDownloadFilterContext = useMemo(() => ({
    retailerId: filters.retailer,
    cycleId: filters.cycle,
    campaignId: filters.campaign,
    retailerName: retailers.find((retailer: any) => retailer.id.toString() === filters.retailer)?.name,
    cycleName: cycles.find((cycle: any) => cycle.id.toString() === filters.cycle)?.name,
    campaignName: campaigns.find((campaign: any) => campaign.id.toString() === filters.campaign)?.name,
    taskType: filters.taskType,
    reviewStatus: filters.reviewStatus,
  }), [filters, retailers, cycles, campaigns]);

  const galleryDownloadNamingMode = useMemo(
    () => resolveGalleryDownloadNamingMode(galleryDownloadFilterContext),
    [galleryDownloadFilterContext],
  );

  const downloadSelectedAsZip = useCallback(async () => {
    const selected = Array.from(selectedImagesById.values());
    if (selected.length === 0) return;

    setIsDownloading(true);
    setDownloadProgress(0);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const zip = new JSZip();
      const usedNames = new Map<string, number>();

      for (let i = 0; i < selected.length; i++) {
        if (controller.signal.aborted) return;

        const img = selected[i];
        const url = img.downloadUrl || img.assetUrl || img.url;
        const fileName = buildGalleryDownloadEntryFilename(
          usedNames,
          {
            id: img.id,
            storeName: img.storeName,
            campaignName: img.campaignName,
          },
          galleryDownloadNamingMode,
        );

        const response = await fetch(url, { signal: controller.signal });
        const blob = await response.blob();
        // Defensive: never write nested ZIP paths even if upstream names contain slashes.
        zip.file(fileName.split(/[/\\]/).pop() ?? fileName, blob);

        setDownloadProgress(Math.round(((i + 1) / selected.length) * 90));
      }

      if (controller.signal.aborted) return;

      setDownloadProgress(95);
      const content = await zip.generateAsync({ type: 'blob' });
      setDownloadProgress(100);

      const link = document.createElement('a');
      link.href = URL.createObjectURL(content);
      link.download = buildGalleryDownloadZipFilename(galleryDownloadFilterContext);
      link.click();
      URL.revokeObjectURL(link.href);

      toast({ title: "Download complete", description: `${selected.length} image${selected.length > 1 ? 's' : ''} downloaded.` });
      exitSelectionMode();
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      toast({ title: "Download failed", description: err?.message || "Something went wrong", variant: "destructive" });
    } finally {
      setIsDownloading(false);
      setDownloadProgress(0);
      abortRef.current = null;
    }
  }, [
    selectedImagesById,
    toast,
    exitSelectionMode,
    galleryDownloadFilterContext,
    galleryDownloadNamingMode,
  ]);

  // 4. Operators — always fetched (lightweight list)
  const { data: operators = [] } = useQuery<any[]>({
    queryKey: ['/api/users/operators'],
    queryFn: async () => apiJson('/api/users/operators'),
    staleTime: 60_000,
  });

  // 5. Stores — always fetched (lightweight list)
  const { data: stores = [] } = useQuery<any[]>({
    queryKey: ['/api/stores', { retailerId: filters.retailer }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.retailer !== 'all') {
        params.set('retailerId', filters.retailer);
      }
      const suffix = params.toString() ? `?${params.toString()}` : '';
      return apiJson(`/api/stores${suffix}`);
    },
    staleTime: 60_000,
  });
  const sortedStores = useMemo(
    () =>
      [...stores].sort((a: any, b: any) =>
        String(a?.name || '').localeCompare(String(b?.name || ''), undefined, { sensitivity: 'base' })
      ),
    [stores]
  );

  // ─── Gallery images fetch ────────────────────────────────────────────

  const { data: imagesData, isLoading, isFetching, isError, error } = useQuery({
    queryKey: ['/api/gallery-proxy', year, filters, currentPage],
    queryFn: async () => {
      const params = buildGalleryParams(currentPage, pageSize);

      const response = await http(`/api/gallery-proxy?${params}`);
      if (!response.ok) throw new Error('Failed to fetch images');
      const data = await response.json();

      return {
        images: data.images?.map((img: any) => mapGalleryImage(img)) || [],
        pagination: {
          currentPage: data.pagination?.currentPage || data.pagination?.page || 1,
          totalPages: data.pagination?.totalPages || Math.ceil((data.pagination?.total || 0) / (data.pagination?.imagesPerPage || data.pagination?.limit || 20)),
          totalImages: data.pagination?.total || data.pagination?.totalImages || 0,
          imagesPerPage: data.pagination?.imagesPerPage || data.pagination?.limit || 20
        }
      };
    },
    enabled: !!year,
  });

  const fetchAllFilteredImages = useCallback(async () => {
    const totalImages = imagesData?.pagination?.totalImages || 0;
    if (totalImages === 0) return [] as ImageData[];

    const totalPages = Math.ceil(totalImages / pageSize);
    const allImages: ImageData[] = [];

    for (let page = 1; page <= totalPages; page++) {
      const params = buildGalleryParams(page, pageSize);
      const response = await http(`/api/gallery-proxy?${params}`);
      if (!response.ok) throw new Error('Failed to fetch all images');
      const data = await response.json();

      const pageImages: ImageData[] = (data.images || []).map((img: any) => mapGalleryImage(img));

      allImages.push(...pageImages);
    }

    return allImages;
  }, [buildGalleryParams, imagesData?.pagination?.totalImages, pageSize]);

  // ─── Filter change handlers ──────────────────────────────────────────

  const handleFilterChange = (key: keyof FilterState, value: string) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };

      // Cascading resets: parent change clears all children
      if (key === 'retailer') {
        newFilters.cycle = 'all';
        newFilters.campaign = 'all';
        newFilters.operator = 'all';
        newFilters.store = 'all';
      } else if (key === 'cycle') {
        newFilters.campaign = 'all';
        newFilters.operator = 'all';
        newFilters.store = 'all';
      } else if (key === 'campaign') {
        newFilters.operator = 'all';
        newFilters.store = 'all';
      } else if (key === 'taskType') {
        newFilters.campaign = 'all';
      }

      return newFilters;
    });
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      retailer: 'all',
      cycle: 'all',
      campaign: 'all',
      operator: 'all',
      store: 'all',
      type: 'all',
      taskType: 'all',
      reviewStatus: 'all',
      search: ''
    });
    setCurrentPage(1);
  };

  // Count active filters for badge
  const activeFilterCount = [
    filters.retailer !== 'all',
    filters.cycle !== 'all',
    filters.campaign !== 'all',
    filters.operator !== 'all',
    filters.store !== 'all',
    filters.type !== 'all',
    filters.taskType !== 'all',
    filters.reviewStatus !== 'all',
    !!filters.search,
  ].filter(Boolean).length;

  const openGoogleMaps = (lat: number, lng: number) => {
    window.open(`https://maps.google.com/?q=${lat},${lng}`, '_blank');
  };

  const images: ImageData[] = imagesData?.images || [];
  const totalFilteredImages = imagesData?.pagination?.totalImages || 0;
  const totalPages = Math.ceil(totalFilteredImages / pageSize);
  const lightboxImage = selectedImageIndex !== null ? images[selectedImageIndex] : null;
  const lightboxLoading = selectedImageIndex !== null && !lightboxImage && isFetching;
  const lightboxOpen = selectedImageIndex !== null && (Boolean(lightboxImage) || lightboxLoading);

  const navigateToGlobalIndex = useCallback((globalIndex: number) => {
    if (totalFilteredImages === 0) return;

    const wrappedIndex = ((globalIndex % totalFilteredImages) + totalFilteredImages) % totalFilteredImages;
    const targetPage = Math.floor(wrappedIndex / pageSize) + 1;
    const indexOnPage = wrappedIndex % pageSize;

    if (targetPage !== currentPage) {
      pendingLightboxIndexRef.current = indexOnPage;
      setCurrentPage(targetPage);
    } else {
      setSelectedImageIndex(indexOnPage);
    }
  }, [currentPage, pageSize, totalFilteredImages]);

  const prevImage = useCallback(() => {
    if (selectedImageIndex === null || totalFilteredImages === 0) return;
    const globalIndex = (currentPage - 1) * pageSize + selectedImageIndex;
    navigateToGlobalIndex(globalIndex - 1);
  }, [selectedImageIndex, currentPage, pageSize, totalFilteredImages, navigateToGlobalIndex]);

  const nextImage = useCallback(() => {
    if (selectedImageIndex === null || totalFilteredImages === 0) return;
    const globalIndex = (currentPage - 1) * pageSize + selectedImageIndex;
    navigateToGlobalIndex(globalIndex + 1);
  }, [selectedImageIndex, currentPage, pageSize, totalFilteredImages, navigateToGlobalIndex]);

  useEffect(() => {
    if (pendingLightboxIndexRef.current === null || images.length === 0) return;
    setSelectedImageIndex(Math.min(pendingLightboxIndexRef.current, images.length - 1));
    pendingLightboxIndexRef.current = null;
  }, [images]);

  useEffect(() => {
    if (pendingLightboxIndexRef.current !== null) return;
    setSelectedImageIndex(null);
  }, [currentPage]);

  useEffect(() => {
    setSelectedImageIndex(null);
  }, [filters, year]);

  useEffect(() => {
    if (selectedImageIndex !== null && !isFetching && !images[selectedImageIndex]) {
      setSelectedImageIndex(null);
    }
  }, [selectedImageIndex, images, isFetching]);

  useEffect(() => {
    if (!lightboxOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        prevImage();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        nextImage();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxOpen, prevImage, nextImage]);

  if (isLoading && !imagesData) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  const totalPagesForPagination = totalPages;
  const allFilteredSelected = totalFilteredImages > 0 && selectedIds.size === totalFilteredImages;
  const selectedPhotoIds = Array.from(selectedImagesById.values())
    .filter((image) => image.type === 'photo')
    .map((image) => image.id);

  return (
    <div className="container mx-auto p-6 space-y-6">
      <PageHeader
        icon={Images}
        title="Image Gallery"
        description={`${imagesData?.pagination?.totalImages || 0} total images`}
      />

      {/* Filters — cascading: Year → Retailer → Cycle → Campaign, then Operator / Store / Type */}
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="ml-1">{activeFilterCount} active</Badge>
              )}
            </span>
            {activeFilterCount > 0 && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground hover:text-foreground">
                Clear all
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Primary drill-down row: Year → Retailer → Cycle → Campaign */}
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">Year</Label>
              <YearFilter showIcon={false} />
            </div>

            <div className="min-w-[200px]">
              <Label className="text-xs text-muted-foreground mb-1 block">Retailer</Label>
              <Select value={filters.retailer} onValueChange={(value) => handleFilterChange('retailer', value)}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Select retailer…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All retailers</SelectItem>
                  {retailers.map((r: any) => (
                    <SelectItem key={r.id} value={r.id.toString()}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-[200px]">
              <Label className="text-xs text-muted-foreground mb-1 block">Cycle</Label>
              <Select
                value={filters.cycle}
                onValueChange={(value) => handleFilterChange('cycle', value)}
                disabled={!retailerSelected}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder={retailerSelected ? "Select cycle…" : "Choose retailer first"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All cycles</SelectItem>
                  {cycles.map((c: any) => (
                    <SelectItem key={c.id} value={c.id.toString()}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-[220px]">
              <Label className="text-xs text-muted-foreground mb-1 block">Campaign</Label>
              <Select
                value={filters.campaign}
                onValueChange={(value) => handleFilterChange('campaign', value)}
                disabled={!cycleSelected}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder={cycleSelected ? "Select campaign…" : "Choose cycle first"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All campaigns</SelectItem>
                  {campaigns.map((c: any) => (
                    <SelectItem key={c.id} value={c.id.toString()}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Secondary filters row: Operator, Store, Type, Search */}
          <div className="flex flex-wrap items-end gap-4 pt-2 border-t">
            <div className="min-w-[180px]">
              <Label className="text-xs text-muted-foreground mb-1 block">Operator</Label>
              <Select value={filters.operator} onValueChange={(value) => handleFilterChange('operator', value)}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All operators" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All operators</SelectItem>
                  {operators.map((o: any) => (
                    <SelectItem key={o.id} value={o.id.toString()}>
                      {o.name || o.username}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-[180px]">
              <Label className="text-xs text-muted-foreground mb-1 block">Store</Label>
              <Select value={filters.store} onValueChange={(value) => handleFilterChange('store', value)}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All stores" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All stores</SelectItem>
                  {sortedStores.map((s: any) => (
                    <SelectItem key={s.id} value={s.id.toString()}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-[140px]">
              <Label className="text-xs text-muted-foreground mb-1 block">Type</Label>
              <Select value={filters.type} onValueChange={(value) => handleFilterChange('type', value)}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="photo">Photos</SelectItem>
                  <SelectItem value="signature">Signatures</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-[160px]">
              <Label className="text-xs text-muted-foreground mb-1 block">Task Type</Label>
              <Select value={filters.taskType} onValueChange={(value) => handleFilterChange('taskType', value)}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All task types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All task types</SelectItem>
                  <SelectItem value="POSTING">Posting</SelectItem>
                  <SelectItem value="REMOVAL">Removal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-[160px]">
              <Label className="text-xs text-muted-foreground mb-1 block">Status</Label>
              <Select value={filters.reviewStatus} onValueChange={(value) => handleFilterChange('reviewStatus', value)}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="retailer_ready">Retailer Ready</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-[200px] flex-1 max-w-sm">
              <Label className="text-xs text-muted-foreground mb-1 block">Search</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search names, notes…"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-8 h-9"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Selection toolbar */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant={selectionMode ? "default" : "outline"}
            size="sm"
            onClick={() => selectionMode ? exitSelectionMode() : setSelectionMode(true)}
          >
            <CheckSquare className="h-4 w-4 mr-1" />
            {selectionMode ? "Cancel Selection" : "Select"}
          </Button>

          {selectionMode && (
            <>
              <Button
                variant="outline"
                size="sm"
                disabled={isSelectingAll || isDownloading || totalFilteredImages === 0}
                onClick={async () => {
                  if (allFilteredSelected) {
                    setSelectedIds(new Set());
                    setSelectedImagesById(new Map());
                    return;
                  }

                  setIsSelectingAll(true);
                  try {
                    const allImages = await fetchAllFilteredImages();
                    setSelectedIds(new Set(allImages.map(img => img.id)));
                    setSelectedImagesById(new Map(allImages.map(img => [img.id, img])));
                  } catch (err: any) {
                    toast({
                      title: "Select all failed",
                      description: err?.message || "Could not load all filtered images",
                      variant: "destructive",
                    });
                  } finally {
                    setIsSelectingAll(false);
                  }
                }}
              >
                {isSelectingAll ? "Selecting..." : (allFilteredSelected ? "Deselect All" : "Select All")}
              </Button>

              {selectedIds.size > 0 && (
                <span className="text-sm text-muted-foreground">
                  {selectedIds.size} selected{totalFilteredImages > images.length && allFilteredSelected ? " (all pages)" : ""}
                </span>
              )}
            </>
          )}
        </div>

        {selectionMode && selectedIds.size > 0 && (
          <div className="flex justify-start lg:justify-end">
            <div className="bg-background border rounded-lg shadow-sm px-4 py-3 flex flex-wrap items-center gap-3">
              {isDownloading ? (
                <div className="flex items-center gap-3 min-w-[280px]">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <div className="flex-1">
                    <Progress value={downloadProgress} className="h-2" />
                  </div>
                  <span className="text-sm text-muted-foreground whitespace-nowrap">{downloadProgress}%</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => abortRef.current?.abort()}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <span className="text-sm font-medium">{selectedIds.size} image{selectedIds.size > 1 ? 's' : ''} selected</span>
                  <Button
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700"
                    disabled={selectedPhotoIds.length === 0 || markRetailerReadyMutation.isPending}
                    onClick={() => markRetailerReadyMutation.mutate(selectedPhotoIds)}
                  >
                    {markRetailerReadyMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Star className="h-4 w-4 mr-1" />
                    )}
                    Mark Retailer Ready
                  </Button>
                  <Button size="sm" onClick={() => downloadSelectedAsZip()}>
                    <Download className="h-4 w-4 mr-1" />
                    Download ZIP
                  </Button>
                  <Button variant="ghost" size="sm" onClick={exitSelectionMode}>
                    <X className="h-4 w-4 mr-1" />
                    Clear
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Image Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
        {images.map((image, index) => {
          const isSelected = selectedIds.has(image.id);

          const cardElement = (
            <Card
              className={`cursor-pointer hover:shadow-lg transition-shadow ${selectionMode && isSelected ? 'ring-2 ring-primary' : ''}`}
              onClick={selectionMode ? () => toggleSelection(image) : () => setSelectedImageIndex(index)}
            >
              <div className="aspect-[3/4] bg-gray-100 relative overflow-hidden rounded-t-lg">
                <img
                  src={image.thumbnailUrl || image.assetUrl || image.url}
                  alt={`${image.type} from ${image.storeName}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute top-2 left-2">
                  <Badge variant={image.type === 'photo' ? 'default' : 'secondary'}>
                    {image.type === 'photo' ? (
                      <Camera className="h-3 w-3 mr-1" />
                    ) : (
                      <FileSignature className="h-3 w-3 mr-1" />
                    )}
                    {image.type}
                  </Badge>
                </div>
                {selectionMode && (
                  <div className="absolute top-2 right-2">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleSelection(image)}
                      onClick={(e) => e.stopPropagation()}
                      className="h-5 w-5 border-2 border-white bg-white/80 data-[state=checked]:bg-primary data-[state=checked]:border-primary shadow"
                    />
                  </div>
                )}
                {!selectionMode && image.isFavourite && (
                  <div className="absolute top-2 right-2" title="Favourited for reporting">
                    <Star className="h-5 w-5 fill-yellow-400 text-yellow-400 drop-shadow" />
                  </div>
                )}
                {image.reviewStatus && (
                  <div className="absolute bottom-2 left-2 max-w-[calc(100%-1rem)]">
                    <Badge
                      title={image.attributionLabel ?? undefined}
                      className={
                      image.reviewStatus === 'admin_recovery'
                        ? 'bg-amber-600 text-white border-0 text-[10px] px-1.5 py-0.5 max-w-full truncate'
                        : image.reviewStatus === 'retailer_ready'
                          ? 'bg-blue-600 text-white border-0 text-[10px] px-1.5 py-0.5'
                          : image.reviewStatus === 'approved'
                            ? 'bg-green-600 text-white border-0 text-[10px] px-1.5 py-0.5'
                            : 'bg-red-600 text-white border-0 text-[10px] px-1.5 py-0.5'
                    }>
                      {image.reviewStatus === 'admin_recovery'
                        ? (image.attributionLabel ?? `ReAction Admin on behalf of ${image.operatorName}`)
                        : image.reviewStatus === 'retailer_ready'
                          ? 'Retailer Ready'
                          : image.reviewStatus === 'approved'
                            ? 'Approved'
                            : 'Rejected'}
                    </Badge>
                  </div>
                )}
              </div>
              <CardContent className="p-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Building2 className="h-3 w-3" />
                    <span className="truncate">{image.storeName}</span>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <User className="h-3 w-3 shrink-0" />
                    <span className="truncate" title={image.attributionLabel ?? image.operatorName}>
                      {image.attributionLabel ?? image.operatorName}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    <span>{format(new Date(image.createdAt), 'MMM d, yyyy')}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          );

          return <div key={image.id}>{cardElement}</div>;
        })}
      </div>

      <Dialog open={lightboxOpen} onOpenChange={(open) => !open && setSelectedImageIndex(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto px-12 sm:px-14">
          {lightboxLoading && (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}
          {lightboxImage && selectedImageIndex !== null && (
            <>
              {totalFilteredImages > 1 && (
                <>
                  <button
                    type="button"
                    className="absolute left-3 top-1/2 -translate-y-1/2 z-10 flex h-10 w-10 items-center justify-center rounded-full border bg-background/95 shadow-md hover:bg-muted"
                    onClick={prevImage}
                    aria-label="Previous image"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 z-10 flex h-10 w-10 items-center justify-center rounded-full border bg-background/95 shadow-md hover:bg-muted"
                    onClick={nextImage}
                    aria-label="Next image"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                </>
              )}

              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 pr-8">
                  {lightboxImage.type === 'photo' ? (
                    <Camera className="h-5 w-5" />
                  ) : (
                    <FileSignature className="h-5 w-5" />
                  )}
                  {lightboxImage.attributionLabel
                    ? 'Placement photo — on behalf of operator'
                    : lightboxImage.type === 'photo'
                      ? 'Placement Photo'
                      : 'Digital Signature'}
                  {totalFilteredImages > 1 && (
                    <span className="text-sm font-normal text-muted-foreground ml-auto">
                      {(currentPage - 1) * pageSize + selectedImageIndex + 1} of {totalFilteredImages}
                    </span>
                  )}
                </DialogTitle>
              </DialogHeader>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <img
                    src={lightboxImage.assetUrl || lightboxImage.url}
                    alt={`${lightboxImage.type} from ${lightboxImage.storeName}`}
                    className="w-full rounded-lg shadow-lg"
                  />

                  <div className="flex flex-wrap gap-2 mt-4">
                    <Button
                      size="sm"
                      onClick={() => {
                        window.open(lightboxImage.downloadUrl || lightboxImage.assetUrl || lightboxImage.url, '_blank');
                      }}
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      View Full Size
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = lightboxImage.downloadUrl || lightboxImage.assetUrl || lightboxImage.url;
                        link.download = `${buildGalleryDownloadImageBasename(
                          {
                            id: lightboxImage.id,
                            storeName: lightboxImage.storeName,
                            campaignName: lightboxImage.campaignName,
                          },
                          galleryDownloadNamingMode,
                        )}.jpg`;
                        link.click();
                      }}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </Button>
                    {lightboxImage.type === 'photo' && (
                      <Button
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700"
                        disabled={
                          markRetailerReadyMutation.isPending ||
                          isGalleryPhotoRetailerReadyComplete(lightboxImage)
                        }
                        onClick={() => markRetailerReadyMutation.mutate([lightboxImage.id])}
                      >
                        {markRetailerReadyMutation.isPending ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <Star className="h-4 w-4 mr-1" />
                        )}
                        {isGalleryPhotoRetailerReadyComplete(lightboxImage)
                          ? 'Retailer Ready'
                          : 'Mark as Retailer Ready'}
                      </Button>
                    )}
                    {lightboxImage.gallerySource !== 'admin_recovery' && lightboxImage.reviewStatus !== 'admin_recovery' && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="destructive">
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Image</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete this image? This action cannot be undone.
                              The image will be permanently removed from the system.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => {
                                deleteImageMutation.mutate(lightboxImage.id, {
                                  onSuccess: () => setSelectedImageIndex(null),
                                });
                              }}
                              disabled={deleteImageMutation.isPending}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              {deleteImageMutation.isPending ? "Deleting..." : "Delete Image"}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  {lightboxImage.isFavourite && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-md">
                      <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      <span className="text-sm font-medium text-yellow-700">Favourited for reporting</span>
                    </div>
                  )}

                  <div>
                    <Label className="text-sm font-medium">Campaign</Label>
                    <p className="text-sm text-muted-foreground">{lightboxImage.campaignName}</p>
                  </div>

                  <div>
                    <Label className="text-sm font-medium">Store</Label>
                    <p className="text-sm text-muted-foreground">{lightboxImage.storeName}</p>
                  </div>

                  {lightboxImage.attributionLabel ? (
                    <>
                      <div>
                        <Label className="text-sm font-medium">Attribution</Label>
                        <p className="text-sm text-muted-foreground">{lightboxImage.attributionLabel}</p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium">On behalf of</Label>
                        <p className="text-sm text-muted-foreground">{lightboxImage.operatorName}</p>
                      </div>
                      {lightboxImage.recordedByAdminName && (
                        <div>
                          <Label className="text-sm font-medium">Recorded by</Label>
                          <p className="text-sm text-muted-foreground">{lightboxImage.recordedByAdminName}</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div>
                      <Label className="text-sm font-medium">Operator</Label>
                      <p className="text-sm text-muted-foreground">{lightboxImage.operatorName}</p>
                    </div>
                  )}

                  <div>
                    <Label className="text-sm font-medium">Cycle</Label>
                    <p className="text-sm text-muted-foreground">{lightboxImage.cycleLabel || '—'}</p>
                  </div>

                  <div>
                    <Label className="text-sm font-medium">Assignment ID</Label>
                    <p className="text-sm text-muted-foreground">{lightboxImage.assignmentId || '—'}</p>
                  </div>

                  {lightboxImage.storeOwner && (
                    <div>
                      <Label className="text-sm font-medium">Store Owner</Label>
                      <p className="text-sm text-muted-foreground">{lightboxImage.storeOwner}</p>
                    </div>
                  )}

                  <div>
                    <Label className="text-sm font-medium">
                      {lightboxImage.attributionLabel ? 'Recorded' : 'Submitted'}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {format(new Date(lightboxImage.createdAt), 'PPpp')}
                    </p>
                  </div>

                  {lightboxImage.latitude && lightboxImage.longitude && (
                    <div>
                      <Label className="text-sm font-medium">Location</Label>
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-muted-foreground">
                          {lightboxImage.latitude.toFixed(6)}, {lightboxImage.longitude.toFixed(6)}
                        </p>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openGoogleMaps(lightboxImage.latitude!, lightboxImage.longitude!)}
                        >
                          <MapPin className="h-3 w-3 mr-1" />
                          View Map
                        </Button>
                      </div>
                    </div>
                  )}

                  {lightboxImage.notes && (
                    <div>
                      <Label className="text-sm font-medium">Notes</Label>
                      <p className="text-sm text-muted-foreground">{lightboxImage.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {isError && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-lg font-medium mb-2 text-destructive">Could not load gallery</p>
            <p className="text-muted-foreground text-center text-sm">
              {error instanceof Error ? error.message : 'The gallery request failed. Try refreshing the page.'}
            </p>
          </CardContent>
        </Card>
      )}
      {!isError && images.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Camera className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium mb-2">No images found</p>
            <p className="text-muted-foreground text-center">
              Try adjusting your filters or check back later when operators have submitted photos.
              Photos completed by admin on behalf of the operator appear here when cycle, store, or operator filters match.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {totalPagesForPagination > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
          >
            Previous
          </Button>

          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(5, totalPagesForPagination) }, (_, i) => {
              const page = Math.max(1, Math.min(totalPagesForPagination - 4, currentPage - 2)) + i;
              return (
                <Button
                  key={page}
                  variant={page === currentPage ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </Button>
              );
            })}
          </div>

          <Button
            variant="outline"
            disabled={currentPage === totalPagesForPagination}
            onClick={() => setCurrentPage(prev => Math.min(totalPagesForPagination, prev + 1))}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
